import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Download, ExternalLink, Calendar, User, Trash2, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  deleteProposalInvoice,
  downloadProposalInvoiceFile,
  listProposalInvoices,
  viewProposalInvoiceFile,
} from "@/lib/proposalsApi";
import { format } from "date-fns";

interface InvoiceData {
  id: string;
  file_name: string;
  file_size: number | null;
  uploaded_at: string;
  uploaded_by: string | null;
  uploader_name?: string;
}

interface ProposalInvoiceBlockProps {
  proposalId: string;
  currentUserRole?: string | null;
  onInvoiceLoaded?: (hasInvoice: boolean) => void;
}

const CAN_MANAGE_INVOICES_ROLES = new Set(["ceo", "general_manager", "sales_staff", "finance_staff"]);

export const ProposalInvoiceBlock: React.FC<ProposalInvoiceBlockProps> = ({
  proposalId,
  currentUserRole,
  onInvoiceLoaded,
}) => {
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [deletingInvoice, setDeletingInvoice] = useState<InvoiceData | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canManageInvoices = !!currentUserRole && CAN_MANAGE_INVOICES_ROLES.has(currentUserRole);

  useEffect(() => {
    loadInvoices();
  }, [proposalId]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await listProposalInvoices(proposalId);
      const mapped = data.map((invoice) => ({
        id: invoice.id,
        file_name: invoice.file_name,
        file_size: invoice.file_size ?? null,
        uploaded_at: invoice.uploaded_at,
        uploaded_by: invoice.uploaded_by ?? null,
        uploader_name: invoice.uploader_name ?? "Unknown",
      }));
      setInvoices(mapped);
      onInvoiceLoaded?.(mapped.length > 0);
    } catch (error) {
      console.error("Error loading invoices:", error);
      onInvoiceLoaded?.(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoice: InvoiceData) => {
    try {
      setActioningId(invoice.id);
      const { blob, fileName } = await downloadProposalInvoiceFile(proposalId, invoice.id);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName || invoice.file_name || "proposal-invoice.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading invoice:", error);
    } finally {
      setActioningId(null);
    }
  };

  const handleView = async (invoice: InvoiceData) => {
    try {
      const { blob } = await viewProposalInvoiceFile(proposalId, invoice.id);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      console.error("Error viewing invoice:", error);
    }
  };

  const confirmDelete = async () => {
    if (!deletingInvoice) return;
    try {
      setDeleting(true);
      await deleteProposalInvoice(proposalId, deletingInvoice.id);
      setDeletingInvoice(null);
      await loadInvoices();
    } catch (error) {
      console.error("Error deleting invoice:", error);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="h-4 w-4 animate-pulse bg-muted rounded" />
            <span className="text-sm">Loading invoices...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (invoices.length === 0) {
    return null;
  }

  return (
    <>
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Payment Invoice{invoices.length > 1 ? `s (${invoices.length})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-primary/10 bg-background/60 p-3"
            >
              <div className="space-y-1 min-w-0">
                <p className="font-medium text-sm truncate">{invoice.file_name}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(invoice.uploaded_at), "MMM dd, yyyy 'at' HH:mm")}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {invoice.uploader_name}
                  </span>
                  {invoice.file_size && (
                    <span>{(invoice.file_size / 1024 / 1024).toFixed(2)} MB</span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button variant="outline" size="sm" onClick={() => handleView(invoice)}>
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(invoice)}
                  disabled={actioningId === invoice.id}
                >
                  {actioningId === invoice.id ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  Download
                </Button>
                {canManageInvoices && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeletingInvoice(invoice)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <AlertDialog open={!!deletingInvoice} onOpenChange={(open) => !open && setDeletingInvoice(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{deletingInvoice?.file_name}" from this proposal. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProposalInvoiceBlock;
